import type { SosEventResponse } from "@somewear/api";
import { grpc, someGrpc } from "@somewear/grpc";
import type { ActionSetEpic } from "@somewear/model";
import { createActionSetEpicHandler, haltAll } from "@somewear/model";
import type { Action } from "redux";
import type { Epic } from "redux-observable";
import { combineEpics, ofType } from "redux-observable";
import { forkJoin, of } from "rxjs";
import { catchError, map, switchMap, takeUntil } from "rxjs/operators";

import { resolveSosSessions } from "./sos.actions";
import { apiSosEventsError, apiSosEventsRequest, apiSosEventsSuccess } from "./sos.slice";

/*
Get active sos events
 */
const apiSosEventsEpic: Epic<Action<string>> = (action$) =>
	action$.pipe(
		ofType(apiSosEventsRequest.type),
		switchMap(() =>
			grpc.prepareRequest(someGrpc.getActiveSosEvents).pipe(
				map((events) => apiSosEventsSuccess(events.toObject().eventsList)),
				takeUntil(action$.pipe(ofType(haltAll.type))),
				catchError((error) => of(apiSosEventsError(error))),
			),
		),
	);

export const resolveSosSessionsEpic: ActionSetEpic<
	SosEventResponse.AsObject[],
	SosEventResponse.AsObject[]
> = (action$, state$) =>
	createActionSetEpicHandler(action$, state$, resolveSosSessions, (payload) =>
		forkJoin(
			payload.data.map((event) =>
				grpc
					.prepareRequestWithPayload(someGrpc.resolveSos, {
						userId: event.userId,
						sessionId: event.sessionId,
						sandbox: false,
					})
					.pipe(map((response) => response.toObject())),
			),
		),
	);

// The fetch action is a legacy plain Redux action while resolve uses an action-set payload.
// Normalize the latter at this composition boundary so the individual epic remains strongly typed.
const resolveSosSessionsEpicForCombine: Epic<Action, Action> =
	resolveSosSessionsEpic as unknown as Epic<Action, Action>;

export const sosEpics = combineEpics<Action, Action>(
	apiSosEventsEpic,
	resolveSosSessionsEpicForCombine,
);
